// Las dos formas de ir para atrás en un circuito, con jerarquía visual explícita.
//
//   <BackStepLink>  — un paso atrás, dentro del mismo circuito. Una flecha.
//   <FlowExitLink>  — abandonar el circuito y volver a la app que nos mandó,
//                     avisándole que la acción no se completó. Doble flecha.
//
// Las dos son deliberadamente DISCRETAS (texto apagado, sin fondo ni borde). En
// cada pantalla el único elemento en color primario es la acción que lleva al
// paso siguiente, que es lo que el usuario va a querer hacer casi siempre; si
// las salidas compitieran en peso visual con esa, el formulario dejaría de
// tener una lectura obvia.

import { buildFlowExitUrl, type FlowExit } from '@/lib/flow-exit';

const linkClass =
  'inline-flex items-center gap-1.5 text-sm text-text-muted underline-offset-4 ' +
  'transition hover:text-text hover:underline focus:outline-none focus-visible:ring-2 ' +
  'focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background ' +
  'rounded-sm';

function ArrowLeft(): JSX.Element {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M11 5l-5 5 5 5M6 10h9" />
    </svg>
  );
}

/** Doble flecha: "más atrás" que un paso — se sale del circuito entero. */
function ArrowLeftDouble(): JSX.Element {
  return (
    <svg
      width="18"
      height="16"
      viewBox="0 0 24 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 5l-5 5 5 5" />
      <path d="M16 5l-5 5 5 5" />
    </svg>
  );
}

export function BackStepLink({
  onClick,
  label = 'Volver al paso anterior',
}: {
  onClick: () => void;
  label?: string;
}): JSX.Element {
  return (
    <button type="button" onClick={onClick} className={linkClass}>
      <ArrowLeft />
      {label}
    </button>
  );
}

interface FlowExitLinkProps {
  /**
   * A dónde volver. Si no se puede construir la URL (sin destino o malformada)
   * el componente NO se renderiza: mejor no ofrecer la salida que ofrecer una
   * que deja al usuario en la nada.
   */
  exit?: FlowExit | null;
  /**
   * Alternativa a `exit` para pantallas que ya tienen su propia función de
   * salida (ej: Subscribe, que valida la whitelist del return_url server-side
   * y cae a una pantalla final cuando no puede redirigir).
   */
  onExit?: (() => void) | null;
  label?: string;
}

export function FlowExitLink({
  exit,
  onExit,
  label = 'Volver',
}: FlowExitLinkProps): JSX.Element | null {
  const href = exit ? buildFlowExitUrl(exit) : null;
  if (!href && !onExit) return null;

  const go = (): void => {
    if (onExit) {
      onExit();
      return;
    }
    if (href) window.location.href = href;
  };

  return (
    <div className="pt-2 text-center">
      <button
        type="button"
        onClick={go}
        className={linkClass}
        // El label visible es corto a propósito (no debe pesar). La consecuencia
        // real —salir sin terminar— va en el nombre accesible y en el tooltip.
        aria-label={`${label}: salir y volver a la aplicación sin completar esta acción`}
        title="Volvés a la aplicación y le avisamos que la acción no se completó"
      >
        <ArrowLeftDouble />
        {label}
      </button>
    </div>
  );
}
